import {
  PrintProduct,
  PhotoCartGroup,
  CartApiResponse,
  CartUpdateItem,
  Cart
} from "./types";
import { groupCartItemsByPhoto } from "./utils";

export const fetchPrintProductsForPhoto = async (
  photoId: number
): Promise<PrintProduct[]> => {
  try {
    const response = await fetch(
      `/api/v1/photos/${photoId}/print_products`
    );
    if (response.ok) {
      const data = await response.json();
      return data.printProducts || data || [];
    } else {
      console.error("Failed to fetch print products for photo");
      return [];
    }
  } catch (error) {
    console.error("Error fetching print products for photo:", error);
    return [];
  }
};

export const fetchPrintProducts = async (
  eventId: number
): Promise<PrintProduct[]> => {
  try {
    const response = await fetch(
      `/api/v1/events/${eventId}/print_products`
    );
    if (response.ok) {
      const data = await response.json();
      return data.printProducts || data || [];
    } else {
      console.error("Failed to fetch print products");
      return [];
    }
  } catch (error) {
    console.error("Error fetching print products:", error);
    return [];
  }
};

// Fetch full cart with details
export const fetchCart = async (cartId: number | null): Promise<Cart | null> => {
  try {
    if (!cartId) {
      console.log("No cart ID provided");
      return null;
    }
    
    const response = await fetch(`/api/v1/cart/${cartId}/items`);
    if (response.ok) {
      const data: CartApiResponse = await response.json();
      console.log("Fetched cart:", data.cart);
      return data.cart;
    } else {
      console.error("Failed to fetch cart");
      return null;
    }
  } catch (error) {
    console.error("Error fetching cart:", error);
    return null;
  }
};

// Fetch cart items grouped by photo
export const fetchCartItems = async (
  cartId: number | null
): Promise<PhotoCartGroup[]> => {
  try {
    const cart = await fetchCart(cartId);
    if (cart) {
      // Group cart items by photo_id
      const groupedItems = groupCartItemsByPhoto(cart.sales);
      console.log("Grouped cart items:", groupedItems);
      return groupedItems;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error fetching cart items:", error);
    return [];
  }
};

export const fetchPrintProductsForEvent = fetchPrintProducts;

// Update cart items
export const updateCartItems = async (
  cartId: number | null,
  items: CartUpdateItem[]
): Promise<CartApiResponse | null> => {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    
    // Build the URL based on whether cartId is provided
    const url = cartId 
      ? `/api/v1/cart/${cartId}/items`
      : `/api/v1/cart/items`;
    
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken && { 'X-CSRF-Token': csrfToken.content })
      },
      body: JSON.stringify({ items })
    });

    if (response.ok) {
      const data: CartApiResponse = await response.json();
      console.log("Cart updated successfully:", data);
      return data;
    } else {
      console.error("Failed to update cart items");
      return null;
    }
  } catch (error) {
    console.error("Error updating cart items:", error);
    return null;
  }
};

// Remove all products for a specific photo from cart
export const removePhotoFromCart = async (
  cartId: number | null,
  photoId: number,
  salesIds: number[]
): Promise<boolean> => {
  try {
    const itemsToRemove = salesIds.map(id => ({
      id,
      _destroy: true
    }));

    const response = await updateCartItems(cartId, itemsToRemove);
    return response !== null;
  } catch (error) {
    console.error("Error removing photo from cart:", error);
    return false;
  }
};
